import CalendarIcon from "../../../../../assets/svg/calendar-icon";
import ProfileTwoUser from "../../../../../assets/svg/v1/profile-two-user";
import PoundIcon from "../../../../../assets/svg/pound-icon";
import RoomTypeIcon from "../../../../../assets/svg/room-type-icon";
import HomeIcon from "../../../../../assets/svg/home-icon";
import LocationIcon from "../../../../../assets/svg/location-icon";
import UniversityIcon from "../../../../../assets/svg/university-icon";
import LeadStageIcon from "../../../../../assets/svg/lead-stage-icon";
import QuestionAndAnswerIcon from "../../../../../assets/svg/question-and-answer-icon";
import type {
  FilterFieldOption,
  FilterGroupUI,
  FilterConditionUI,
  ViewFormValues,
} from "../../HelpDesk/components/CreateView/utils/constants";
import { DEAL_LABELS } from "./constants";

export {
  type FilterConditionUI,
  type FilterGroupUI,
  type ViewFormValues,
  INITIAL_VIEW_FORM_VALUES,
  createEmptyCondition,
  createEmptyGroup,
  DURATION_OPTIONS,
  EXISTS_VALUE_OPTIONS,
  type SharedWith,
  SHARED_WITH_OPTIONS,
  SHARED_WITH_LABELS,
  type ComparisonOperator,
} from "../../HelpDesk/components/CreateView/utils/constants";

export { viewFormValidationSchema } from "../../HelpDesk/components/CreateView/utils/functions";

// --- Filter Field Options (keys match API payload keys) ---

export const DEALS_VIEW_FILTER_FIELD_OPTIONS: FilterFieldOption[] = [
  { key: "assignee", label: DEAL_LABELS.owner, icon: ProfileTwoUser },
  { key: "stage", label: DEAL_LABELS.stage, icon: LeadStageIcon },
  {
    key: "created_at",
    label: DEAL_LABELS.createdDateRange,
    icon: CalendarIcon,
  },
  { key: "last_import_event", label: "Import Status", icon: LeadStageIcon },
  { key: "updated_at", label: "Last Activity Date Range", icon: CalendarIcon },
  { key: "contract_start", label: "Contract Start Date", icon: CalendarIcon },
  { key: "contract_end", label: "Contract End Date", icon: CalendarIcon },
  { key: "cancellation_effective_date", label: "Cancellation Date", icon: CalendarIcon },
  { key: "cancelled_primary_reason", label: "Cancellation Reason", icon: QuestionAndAnswerIcon },
  { key: "total_amount", label: DEAL_LABELS.amountRange, icon: PoundIcon },
  { key: "room_type", label: "Room Type", icon: RoomTypeIcon },
  { key: "property_name", label: "Property", icon: HomeIcon },
  { key: "city", label: "City", icon: LocationIcon },
  {
    key: "availability_title",
    label: "Availability Title",
    icon: CalendarIcon,
  },
  { key: "academic_year", label: "Academic Year", icon: UniversityIcon },
];

// --- Operators per field ---

export const DEALS_FILTER_FIELD_OPERATORS: Record<
  string,
  { key: string; label: string }[]
> = {
  assignee: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  stage: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  last_import_event: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  created_at: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
  updated_at: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
  contract_start: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
  contract_end: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
  cancellation_effective_date: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
  cancelled_primary_reason: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  academic_year: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  total_amount: [{ key: "range", label: "Is" }],
  room_type: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  property_name: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  city: [
    { key: "contains", label: "Contains" },
    { key: "not_contains", label: "Excludes" },
  ],
  availability_title: [
    { key: "contains", label: "Contains" },
    { key: "not_contains", label: "Excludes" },
  ],
};

// --- Deal Amount Range ---

export const DEAL_AMOUNT_RANGE_PRESETS = [
  { min: 100, max: 500 },
  { min: 500, max: 1000 },
  { min: 1000, max: 2000 },
  { min: 2000, max: 3000 },
  { min: 3000, max: 4000 },
  { min: 4000, max: 5000 },
  { min: 5000, max: 6000 },
];

// --- Value Options Map (static only, API-backed fields resolved in condition row) ---

export const DEALS_VALUE_OPTIONS_MAP: Record<
  string,
  { key: string; label: string }[]
> = {
  last_import_event: [
    { key: "created", label: "Created" },
    { key: "updated", label: "Updated" },
    { key: "unchanged", label: "Unchanged" },
  ],
};

// --- Single vs Array fields ---

export const DEALS_SINGLE_VALUE_FIELDS = [
  "created_at",
  "updated_at",
  "contract_start",
  "contract_end",
  "cancellation_effective_date",
];

export const DEALS_DATE_RANGE_FIELDS = [
  "created_at",
  "updated_at",
  "contract_start",
  "contract_end",
  "cancellation_effective_date",
];

export const DEALS_ARRAY_FIELDS = [
  "assignee",
  "stage",
  "last_import_event",
  "room_type",
  "property_name",
  "city",
  "availability_title",
  "academic_year",
  "cancelled_primary_reason",
];

// Bookings fields whose value is a typeable free-form string. On
// `contains` / `not_contains` they render a text box and send a single string;
// on `contains_any` / `not_in` they fall back to a multi-select (array).
export const DEALS_FREE_TEXT_FIELDS = ["city", "availability_title"];

// Extra multi-select operators offered in Bookings, in addition to the base
// Is / Is not, for every field that supports a value list.
const DEALS_MULTI_SELECT_EXTRA_OPERATORS = [
  { key: "contains_any", label: "Any of" },
  { key: "not_in", label: "None of" },
];

// --- Disabled fields (Coming soon) ---

export const DEALS_DISABLED_FIELDS: Record<string, string> = {};

// --- Helpers ---

export const getDealsOperatorsForField = (
  fieldKey: string
): { key: string; label: string }[] => {
  const baseOperators = DEALS_FILTER_FIELD_OPERATORS[fieldKey] || [];
  const supportsMultiSelect = baseOperators.some(
    (operator) =>
      operator.key === "contains" || operator.key === "not_contains"
  );
  return supportsMultiSelect
    ? [...baseOperators, ...DEALS_MULTI_SELECT_EXTRA_OPERATORS]
    : baseOperators;
};

type ConditionValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | null
  | undefined;

function getDealsConditionPayloadValue(c: FilterConditionUI) {
  if (c.comparison_operator === "exists") return c.value?.[0] === "true";

  if (
    (c.comparison_operator === "contains" ||
      c.comparison_operator === "not_contains") &&
    DEALS_FREE_TEXT_FIELDS.includes(c.key)
  ) {
    return c.value?.[0];
  }
  if (c.key === "total_amount") return c.value.map(Number);
  if (DEALS_ARRAY_FIELDS.includes(c.key)) return c.value;
  if (
    DEALS_DATE_RANGE_FIELDS.includes(c.key) &&
    c.comparison_operator === "range" &&
    (c.value?.length ?? 0) >= 2
  ) {
    return [c.value[0], c.value[1]];
  }
  return c.value?.[0];
}

export function buildDealsFilterGroupsPayload(
  filterGroups: ViewFormValues["filter_groups"]
) {
  return (filterGroups ?? [])
    .filter((group: FilterGroupUI) =>
      group?.conditions?.some(
        (c: FilterConditionUI) => c?.key && c?.value?.length > 0
      )
    )
    .map((group: FilterGroupUI) => ({
      conditions: (group?.conditions ?? [])
        .filter((c: FilterConditionUI) => c?.key && c?.value?.length > 0)
        .map((c: FilterConditionUI) => ({
          key: c.key === "property_name" ? "property_id" : c.key,
          comparison_operator: c.comparison_operator,
          value: getDealsConditionPayloadValue(c),
          is_custom_field: false,
        })),
      logical_operator: group?.logical_operator ?? "and",
    }));
}

// --- Normalize API response back to UI ---

interface RawFilterCondition {
  key?: string;
  comparison_operator?: string;
  value?: ConditionValue;
  is_custom_field?: boolean;
}

interface RawFilterGroup {
  conditions?: RawFilterCondition[];
  logical_operator?: "and" | "or";
}

function normalizeDealsConditionValue(
  key: string,
  value: ConditionValue
): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (value != null) return [String(value)];
  return [];
}

export function normalizeDealsFilterGroups(
  raw: RawFilterGroup[] | undefined
): FilterGroupUI[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((group: RawFilterGroup) => ({
    conditions: (group?.conditions ?? []).map((c: RawFilterCondition) => {
      if (c?.comparison_operator === "exists") {
        const raw = c?.value as unknown;
        const boolVal = raw === true || raw === "true";
        return {
          key: c?.key ?? "",
          comparison_operator: "exists",
          value: [String(boolVal)],
          is_custom_field: false,
        };
      }
      return {
        key: c?.key === "property_id" ? "property_name" : (c?.key ?? ""),
        comparison_operator: c?.comparison_operator ?? "contains",
        value: normalizeDealsConditionValue(c?.key ?? "", c?.value),
        is_custom_field: false,
      };
    }),
    logical_operator: group?.logical_operator ?? "and",
  }));
}

export const getDealsSelectedValuesForField = (
  filterGroups: FilterGroupUI[],
  fieldKey: string
): string[] => {
  return (filterGroups ?? []).flatMap((g) =>
    (g?.conditions ?? [])
      .filter((c) => c?.key === fieldKey && c?.value?.length > 0)
      .flatMap((c) => c?.value ?? [])
  );
};
